import React, { useEffect, useRef, useState } from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import { POPOVER_CONFIG } from 'common/components/AdvancedSearch/AdvancedSearch.constants';
import { SelectedItemChip } from 'common/components/AdvancedSearch/components';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import FPIcon from 'UI/components/atoms/FPIcon';
import SvgCheck from 'UI/res/icons/filled/Check';

import { useStyles } from './CollapsibleChipFilter.styled';

export const CollapsibleChipFilter = ({
  children,
  chipItem,
  getChipLabel,
  getChipPriorityLabel,
  onClear,
  onPriorityChange,
  priorities,
  showClear,
  title,
  triggerLabel
}) => {
  const classes = useStyles();
  const containerRef = useRef(null);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [priorityAnchor, setPriorityAnchor] = useState(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (!(event.target instanceof Element) || !event.target.closest('.MuiPopover-root')) {
          setIsInputVisible(false);
        }
      }
    };
    if (isInputVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isInputVisible]);

  const handleEditClick = event => {
    if (priorities && onPriorityChange) {
      event.stopPropagation();
      setPriorityAnchor(event.currentTarget);
    } else {
      setIsInputVisible(true);
    }
  };

  const handlePrioritySelect = priorityId => {
    onPriorityChange(priorityId);
    setPriorityAnchor(null);
  };

  const handlePopoverClose = () => {
    setPriorityAnchor(null);
  };

  return (
    <div className={classes.root} ref={containerRef}>
      <div className={classes.titleRow}>
        <Typography variant="subtitle2" className={classes.title}>
          {title}
        </Typography>
        <When condition={showClear}>
          <FPActionButton size="small" variant="text" onClick={onClear}>
            {strings.shared.ui.clearAll}
          </FPActionButton>
        </When>
      </div>

      <When condition={!isInputVisible}>
        <button
          type="button"
          className={classes.collapsedContainer}
          onClick={() => setIsInputVisible(true)}
        >
          <span>+</span>
          <span>
            {triggerLabel ??
              strings.formatString(strings.resumeSearch.filters.addFilter, { title })}
          </span>
        </button>
      </When>

      <When condition={!isInputVisible && chipItem}>
        <div className={classes.chipContainer}>
          <SelectedItemChip
            getOptionLabel={getChipLabel}
            getPriorityLabel={getChipPriorityLabel}
            isEditing={Boolean(priorityAnchor)}
            onEdit={handleEditClick}
            onRemove={onClear}
            selectedItem={chipItem}
          />
        </div>
      </When>

      <Popover
        open={Boolean(priorityAnchor) && Boolean(chipItem)}
        anchorEl={priorityAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={POPOVER_CONFIG.anchorOrigin}
        transformOrigin={POPOVER_CONFIG.transformOrigin}
      >
        <Paper className={classes.popoverContent}>
          <Typography variant="subtitle2" className={classes.popoverSectionTitle}>
            {strings.shared.advancedSearch.priority}
          </Typography>
          {(priorities || []).map(priority => (
            <MenuItem
              key={priority.id}
              onClick={() => handlePrioritySelect(priority.id)}
              selected={priority.id === chipItem?.priority}
            >
              <div className={classes.priorityMenuItem}>
                <span>{priority.label}</span>
                {priority.id === chipItem?.priority && (
                  <FPIcon component={SvgCheck} size="sm" color={priority.color} />
                )}
              </div>
            </MenuItem>
          ))}
        </Paper>
      </Popover>

      {children({ isExpanded: isInputVisible })}
    </div>
  );
};
